import React, { useEffect, ComponentProps } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Container, IconButton, ContainerProps } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import clsx from 'clsx';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  InsertDriveFile as InsertDriveFileIcon,
  List as ListIcon,
  Announcement as AnnouncementIcon,
  MenuBook as MenuBookIcon,
  ChevronLeft as ChevronLeftIcon,
  People as PeopleIcon,
  Comment as CommentIcon
} from '@material-ui/icons';

import auth from '../libs/auth';
import { AdminToolsSpeedDial } from '../components/SpeedDial';

const drawerWidth = 275;
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  menuButton: {
    marginRight: theme.spacing(2),
    position: 'absolute',
    left: '25px',
    top: '50%',
    transform: 'translateY(-50%)'
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: `${theme.spacing(3)} 0`,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    minHeight: '100vh',
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
}));

interface Props extends Omit<ContainerProps, 'children'> {
  children: ComponentProps<any>['children']
}

interface NavItem {
  name: string
  path: string
  icon: ComponentProps<any>['children']
}

const AdminLayout = (props: Props) => {
  const { children, ...containerProps } = props;
  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = React.useState(sessionStorage.getItem('sidenavState') === 'open');

  const history = useHistory();
  const session = auth.getSession();

  const logout = () => {
    auth.logout();
    history.push('/admin/login');
  };

  const handleDrawerToggle = () => {
    sessionStorage.setItem('sidenavState', mobileOpen ? 'close' : 'open');
    setMobileOpen(!mobileOpen);
  };

  const isPathActive = (path: string):boolean => {
    if (history.location.pathname) {
      return history.location.pathname.indexOf(path) === 0;
    }
    return false;
  };

  const navItems: NavItem[] = [
    {
      name: 'Courses',
      path: '/admin/courses',
      icon: <MenuBookIcon />
    },
    {
      name: 'Parse Document',
      path: '/admin/document-parser',
      icon: <InsertDriveFileIcon />
    },
    {
      name: 'Investigate Submission',
      path: '/admin/investigate',
      icon: <SearchIcon />
    },
    {
      name: 'Spec Repository',
      path: '/admin/specs',
      icon: <ListIcon />
    },
    {
      name: 'Spec Discussions',
      path: '/admin/spec-comments',
      icon: <CommentIcon />
    },
    {
      name: 'Announcements',
      path: '/admin/announcements',
      icon: <AnnouncementIcon />
    },
    {
      name: 'Students',
      path: '/admin/students',
      icon: <PeopleIcon />
    }
  ];

  const drawer = (
    <List>
      <ListItem
        button
        onClick={handleDrawerToggle}
        href="#/admin/courses">
        <ListItemIcon>
          <ChevronLeftIcon />
        </ListItemIcon>
        <ListItemText primary="Hide" />
      </ListItem>

      <ListItem
        button
        component="a"
        href="#/admin">
        <img src="/banner.png" className="w-100 py-3" alt="Logo" />
      </ListItem>

      {navItems.map((navItem, index) => (
        <ListItem
          button
          key={index}
          component="a"
          selected={isPathActive(navItem.path)}
          href={'#' + navItem.path}>
          <ListItemIcon>{navItem.icon}</ListItemIcon>
          <ListItemText primary={navItem.name} />
        </ListItem>
      ))}
    </List>
  );

  useEffect(() => {
    if (!auth.isAdmin()) history.push('/');
  }, []);

  if (!session) return <></>;

  return (
    <div className={classes.root}>
      <AdminToolsSpeedDial />

      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        open={mobileOpen}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        {drawer}
      </Drawer>

      <main
        className={clsx(classes.content, {
          [classes.contentShift]: mobileOpen,
          [classes.contentShift]: mobileOpen,
        })}
      >

        <div className="position-relative">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>

          <Container>
            <div className="p-3 d-flex justify-content-between">
              <div>&nbsp;</div>

              <div className="d-flex">
                <div className="d-flex flex-column justify-content-center align-items-end">
                  <h4 className="m-0">
                    {session.user.fullname} (#{session.user.userid})
                  </h4>
                  <p className="m-0">
                    <small>{session.user.email}</small>
                  </p>
                </div>
                <div
                  className="pl-2 ml-2 d-flex align-items-center"
                  style={{ borderLeft: '1px solid gray' }}
                >
                  <Button
                    type="button"
                    onClick={logout}
                    variant="contained"
                    size="small"
                    color="primary"
                  >
                    Logout
                  </Button>
                </div>
              </div>
            </div>
          </Container>
        </div>

        <Divider />
        <Container {...containerProps}>
          {children}
        </Container>
      </main>
    </div>
  );
};

export default AdminLayout;
